import Editor, { type OnChange, type OnMount } from '@monaco-editor/react';
import { cn } from '../lib/cn';

const monacoLangMap: Record<string, string> = {
  cpp: 'cpp', c: 'c', java: 'java', python: 'python',
  javascript: 'javascript', go: 'go', postgresql: 'pgsql', sql: 'pgsql',
};

export interface CodeEditorProps {
  value: string;
  language: string;
  theme?: 'vs-dark' | 'light';
  fontSize?: number;
  tabSize?: number;
  readOnly?: boolean;
  onChange?: OnChange;
  onMount?: OnMount;
  onCursorChange?: (line: number, column: number) => void;
  /**
   * Fired with the size of each paste, in characters.
   *
   * Lives here rather than in the caller because `onMount` below hands callers
   * `undefined` for Monaco's namespace argument, leaving them unable to reach
   * the paste API cleanly — and because this is the only place that can measure
   * a paste before `formatOnPaste` rewrites the range.
   */
  onPaste?: (chars: number) => void;
  className?: string;
  height?: string;
}

export function CodeEditor({
  value, language, theme = 'vs-dark', fontSize = 14, tabSize = 4,
  readOnly = false, onChange, onMount, onCursorChange, onPaste, className, height = '100%',
}: CodeEditorProps) {
  return (
    <div className={cn('h-full w-full overflow-hidden', className)}>
      <Editor
        height={height}
        language={monacoLangMap[language] ?? language}
        value={value}
        theme={theme}
        onChange={onChange}
        onMount={(editor) => {
          onMount?.(editor, undefined as any);
          if (onCursorChange) {
            editor.onDidChangeCursorPosition((e: { position: { lineNumber: number; column: number } }) => {
              onCursorChange(e.position.lineNumber, e.position.column);
            });
          }
          if (onPaste) {
            editor.onDidPaste((e: { range: unknown }) => {
              // Measure the pasted text from the model rather than trusting the
              // clipboard: what actually landed in the buffer is what matters,
              // and the clipboard may hold something the editor rejected.
              const pasted = editor.getModel()?.getValueInRange(e.range as never) ?? '';
              onPaste(pasted.length);
            });
          }
        }}
        options={{
          fontSize, tabSize, readOnly,
          minimap: { enabled: false },
          scrollBeyondLastLine: false,
          wordWrap: 'on',
          automaticLayout: true,
          formatOnPaste: true,
          suggestOnTriggerCharacters: !readOnly,
          lineNumbers: 'on',
          renderLineHighlight: 'all',
          smoothScrolling: true,
          cursorBlinking: 'smooth',
        }}
      />
    </div>
  );
}
